// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Il magazzino materie prime"
// Centro assistenza → guida per operatori e amministratori, solo piano TRACE.
//
// Cosa succede ai lotti DOPO l'ingresso. Il gesto del ricevimento sta nella
// guida precedente ("Registrare una consegna").
//
// Il punto concettuale da non tradire: questo NON e' un gestionale di
// magazzino. Le quantita' non calano da sole, il consumo e' STIMATO dalle
// ricette delle lavorazioni che agganciano il lotto. Ogni volta che si e'
// provato a raccontarlo come un saldo si e' scritta una cosa falsa.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette copiate dal
// codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ............ App.js, GestioneMagazzinoMP
//   Stati ................ STATO_MAG_LABEL: IN_MAGAZZINO | IN_USO | ESAURITO
//   Azioni ............... "Metti in uso" / "Rimetti in magazzino" /
//                          "Segna esaurito" / "Correggi" / "Dettaglio consumi"
//   Correggi ............. aggiunto il 18/08/2026 (prima un dato sbagliato al
//                          ricevimento non era piu' rimediabile). Frontend:
//                          apriCorreggi/confermaCorreggi. Backend: PUT
//                          /magazzino-mp/:id, che ora accetta anche quantita,
//                          unitaMisura, lottoFornitore.
//   Consumo stimato ...... GET /magazzino-mp/:id/consumo; il riquadro dice
//                          "Giacenza teorica stimata" e "Residuo X − prenotato Y"
//   Proprieta' ........... committente_nome dalla bolla; filtro "Proprietà"
//                          visibile solo se l'azienda ha merce di terzi
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'm0', label: 'Che cosa è questa schermata' },
  { id: 'm1', label: '1. Leggere un lotto' },
  { id: 'm2', label: '2. I tre stati' },
  { id: 'm3', label: '3. Le scadenze' },
  { id: 'm4', label: '4. Quanto ne è rimasto' },
  { id: 'm5', label: '5. Correggere un lotto' },
  { id: 'm6', label: '6. La merce di terzi' },
  { id: 'm7', label: 'Le trappole' },
  { id: 'm8', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const BtnFinto = ({ children, kind = 'neutro' }) => {
  const c = {
    blu:    { bg: LT.blu, fg: '#fff',  bd: LT.blu },
    neutro: { bg: LT.n0,  fg: LT.n700, bd: LT.n300 },
  }[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '5px 11px', borderRadius: LT.r.md, fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const DatoGriglia = ({ etichetta, valore, colore }) => (
  <div>
    <div style={{
      fontSize: 10, color: LT.n600, textTransform: 'uppercase',
      letterSpacing: 0.3, fontWeight: 700,
    }}>{etichetta}</div>
    <div style={{ fontSize: 12.5, color: colore || LT.navy, fontWeight: 600, marginTop: 2 }}>
      {valore}
    </div>
  </div>
);

// La card di un lotto, come compare in elenco.
const CardLotto = ({ fascia, ingrediente, lottoInterno, lottoForn, chips, dati, azioni }) => (
  <div style={{
    display: 'flex', background: LT.n0, border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, overflow: 'hidden', marginBottom: 10,
  }}>
    <div style={{ width: 5, alignSelf: 'stretch', background: fascia }} />
    <div style={{ flex: 1, padding: 12, minWidth: 0 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 9,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>{ingrediente}</div>
          <div style={{ fontSize: 11.5, color: LT.n600, fontFamily: LT.mono, marginTop: 2 }}>
            {lottoInterno}
            {lottoForn && <span style={{ marginLeft: 8, opacity: 0.75 }}>· forn. {lottoForn}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{chips}</div>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        gap: 9, padding: '9px 11px', background: LT.n50,
        borderRadius: LT.r.md, marginBottom: 9,
      }}>{dati}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>{azioni}</div>
    </div>
  </div>
);

export default function MagazzinoMp() {
  return (
    <GuidaLayout
      titolo="Il magazzino materie prime"
      sottotitolo="L'elenco dei lotti che sono entrati: dove sono nel loro ciclo di vita, quando scadono, quanto ne resta secondo le lavorazioni che li hanno usati. Non è un gestionale di magazzino, ed è importante capire perché."
      durata="~ 12 minuti"
      destinatario="Per operatori e amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* m0 */}
      <H id="m0">Che cosa è questa schermata</H>

      <P>
        Ogni riga di ogni bolla di ingresso diventa un <Strong>lotto</Strong> qui dentro.{' '}
        <Code>Magazzino materie prime</Code>, nel gruppo Entrate, è l'elenco di tutti quei lotti,
        dal più vicino a scadere in giù.
      </P>
      <P>
        Serve a rispondere a tre domande che tornano ogni settimana: <Strong>che cosa ho in
        casa</Strong>, <Strong>che cosa sta per scadere</Strong> e <Strong>dove è finito quel
        lotto</Strong> quando qualcuno lo chiede.
      </P>

      <Callout kind="warn" titolo="Non è un gestionale di magazzino, ed è voluto">
        Le quantità <Strong>non calano da sole</Strong> quando usate un lotto in produzione.
        Restano quelle che avete ricevuto. Il consumo che vedete è <Strong>stimato</Strong>:
        calcolato dalle ricette delle lavorazioni che hanno agganciato quel lotto. Non è una
        mancanza da colmare — è la scelta che permette di registrare la realtà anche quando i
        conti non tornano, invece di bloccarvi con un "giacenza insufficiente" mentre avete il
        prodotto in mano.
      </Callout>

      <P>
        La conseguenza pratica: questa schermata vi dice <em>cosa è entrato e cosa ne è stato
        fatto</em>, non un saldo contabile. Per il saldo esistono il vostro gestionale e
        l'inventario fisico. Per la rintracciabilità serve questo.
      </P>

      {/* m1 */}
      <H id="m1" cat="temperature">1. Leggere un lotto</H>

      <P>
        In alto quattro numeri — <Strong>In magazzino</Strong>, <Strong>In uso</Strong>,{' '}
        <Strong>Esauriti</Strong>, <Strong>Scaduti</Strong> — che contano i lotti, non le
        quantità. Sotto, una card per lotto.
      </P>

      <MockFrame
        url="app.labthree.it — Magazzino materie prime"
        markers={[
          { n: 1, top: '17%', left: '88%', titolo: 'Scaduti',
            testo: 'L’unico numero che è un allarme: diventa rosso appena è maggiore di zero.' },
          { n: 2, top: '55%', left: '6%', titolo: 'La fascia colorata',
            testo: 'Rossa se scaduto, verde se in uso, blu se in magazzino, grigia se esaurito. Si legge da lontano.' },
          { n: 3, top: '70%', left: '50%', titolo: 'La griglia grigia',
            testo: 'I dati del ricevimento: quantità, scadenza, fornitore, proprietario se non è vostro, e la bolla di provenienza.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo
            titolo="Magazzino materie prime"
            sottotitolo="Lotti ingredienti ricevuti · stato, scadenze e consumi stimati"
          />

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12,
          }}>
            {[
              ['In magazzino', '14', LT.navy],
              ['In uso', '3', LT.navy],
              ['Esauriti', '61', LT.navy],
              ['Scaduti', '2', LT.rosso],
            ].map(([lab, n, col]) => (
              <div key={lab} style={{
                background: LT.n0, border: `1px solid ${LT.n200}`,
                borderRadius: LT.r.md, padding: '9px 11px',
              }}>
                <div style={{
                  fontSize: 9.5, color: LT.n600, textTransform: 'uppercase',
                  letterSpacing: 0.4, fontWeight: 700,
                }}>{lab}</div>
                <div style={{ fontSize: 19, fontWeight: 700, color: col }}>{n}</div>
              </div>
            ))}
          </div>

          <CardLotto
            fascia={LT.verde}
            ingrediente="Sale marino grosso"
            lottoInterno="L.030826"
            lottoForn="SL-2026-114"
            chips={<ChipFinto tono="verde">In uso</ChipFinto>}
            dati={
              <>
                <DatoGriglia etichetta="Quantità" valore="800 kg" />
                <DatoGriglia etichetta="Scadenza" valore="03/08/2028" />
                <DatoGriglia etichetta="Fornitore" valore="Saline Trapani Srl" />
                <DatoGriglia etichetta="Bolla" valore="#2026/391 · 03/08/2026" />
              </>
            }
            azioni={
              <>
                <BtnFinto>Rimetti in magazzino</BtnFinto>
                <BtnFinto>Segna esaurito</BtnFinto>
                <BtnFinto>Correggi</BtnFinto>
                <span style={{ marginLeft: 'auto' }}><BtnFinto>Dettaglio consumi</BtnFinto></span>
              </>
            }
          />

          <CardLotto
            fascia={LT.rosso}
            ingrediente="Panna fresca"
            lottoInterno="L.120826"
            lottoForn="P4471"
            chips={
              <>
                <ChipFinto tono="rosso">Scaduto</ChipFinto>
                <ChipFinto tono="blu">In magazzino</ChipFinto>
              </>
            }
            dati={
              <>
                <DatoGriglia etichetta="Quantità" valore="20 l" />
                <DatoGriglia etichetta="Scadenza" valore="16/08/2026" colore={LT.rosso} />
                <DatoGriglia etichetta="Fornitore" valore="Latteria Val Padana" />
                <DatoGriglia etichetta="Bolla" valore="#2026/402 · 12/08/2026" />
              </>
            }
            azioni={
              <>
                <BtnFinto kind="blu">Metti in uso</BtnFinto>
                <BtnFinto>Correggi</BtnFinto>
                <span style={{ marginLeft: 'auto' }}><BtnFinto>Dettaglio consumi</BtnFinto></span>
              </>
            }
          />
        </Schermo>
      </MockFrame>

      <P>
        Il campo <Tasto>Cerca</Tasto> guarda ingrediente, lotto interno, lotto del fornitore,
        nome del fornitore e nome del committente. La tendina <Tasto>Stato</Tasto> restringe ai
        soli lotti di un certo stato.
      </P>

      {/* m2 */}
      <H id="m2" cat="temperature">2. I tre stati</H>

      <P>
        Un lotto ne ha sempre uno e uno solo. Non sono un obbligo burocratico: servono a far
        capire a colpo d'occhio, a chi entra in cella, cosa può aprire e cosa no.
      </P>

      <Passo n="1" titolo="In magazzino">
        <P style={{ margin: '4px 0' }}>
          Lo stato di partenza: è arrivato, è a posto, nessuno l'ha ancora aperto. Fascia blu.
        </P>
      </Passo>

      <Passo n="2" titolo="In uso">
        <P style={{ margin: '4px 0' }}>
          Il collo è aperto e ci si sta attingendo. Fascia verde. Si arriva qui col pulsante{' '}
          <Tasto>Metti in uso</Tasto>, e si torna indietro con{' '}
          <Tasto>Rimetti in magazzino</Tasto> se lo avete marcato per sbaglio.
        </P>
      </Passo>

      <Passo n="3" titolo="Esaurito">
        <P style={{ margin: '4px 0' }}>
          Finito. Il lotto non sparisce — resta nell'elenco, un po' sbiadito, perché serve allo
          storico. Ci si arriva con <Tasto>Segna esaurito</Tasto>.
        </P>
      </Passo>

      <Callout kind="tip" titolo="Spesso non dovete cambiarli a mano">
        Dalla schermata della lavorazione, quando dichiarate che un lotto è finito e passate al
        successivo, il programma marca da sé il vecchio come esaurito e il nuovo come in uso. In
        un ciclo normale di lavoro questa schermata la aprite per guardare, non per cliccare.
      </Callout>

      <Callout kind="info" titolo="“Esaurito” non è un blocco">
        Un lotto esaurito resta selezionabile in una lavorazione. È voluto: capita di accorgersi
        il giorno dopo di aver usato l'ultimo pezzo di un lotto già chiuso, e impedirlo
        significherebbe costringere a non registrarlo. Lo stato è un'informazione, non un
        divieto.
      </Callout>

      {/* m3 */}
      <H id="m3" cat="temperature">3. Le scadenze</H>

      <P>
        L'elenco è ordinato per <Strong>scadenza crescente</Strong>: in cima c'è sempre quello
        che scade prima. Non è un dettaglio estetico — è il motivo per cui vale la pena aprire
        questa schermata il lunedì mattina.
      </P>
      <P>
        Un lotto scaduto prende la fascia rossa, il contrassegno{' '}
        <ChipFinto tono="rosso">Scaduto</ChipFinto> e la data in rosso, e viene contato nel KPI{' '}
        <Strong>Scaduti</Strong>. La regola è secca: data di scadenza precedente a oggi e stato
        diverso da esaurito.
      </P>

      <Callout kind="warn" titolo="Il programma non blocca un lotto scaduto">
        Potete usarlo in una lavorazione. Segnalarlo sì, impedirlo no — la decisione è vostra e
        la responsabilità pure. Se un lotto scaduto è stato scartato, la cosa corretta da fare è
        marcarlo <Strong>esaurito</Strong>: esce dal conteggio degli scaduti e resta nello
        storico.
      </Callout>

      <Callout kind="tip" titolo="Non tutte le materie prime hanno una scadenza">
        Il campo si può lasciare vuoto: un lotto senza scadenza non compare mai fra gli scaduti e
        si ordina in fondo. Per sale, zuccheri e simili è la scelta giusta — mettere una data
        finta per riempire il campo peggiora le cose.
      </Callout>

      {/* m4 */}
      <H id="m4" cat="produzione">4. Quanto ne è rimasto</H>

      <P>
        Il pulsante <Tasto>Dettaglio consumi</Tasto> apre, sotto la card, il conto di quel lotto:
        quanto ne è entrato, quanto ne hanno prenotato le lavorazioni, quanto dovrebbe
        avanzarne.
      </P>

      <MockFrame
        url="app.labthree.it — Dettaglio consumi di un lotto"
        didascalia="Il numero grande è una stima, non una misura. Se diventa rosso vuol dire che le lavorazioni hanno prenotato più di quanto è entrato."
      >
        <Schermo>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 13px', marginBottom: 12,
            background: LT.verdeSoft, border: `1px solid ${LT.verde}33`,
            borderRadius: LT.r.md, flexWrap: 'wrap',
          }}>
            <Ico d={I.chart} size={17} stroke={LT.verde} />
            <div style={{ fontSize: 12.5, color: LT.verde, fontWeight: 600 }}>
              Giacenza teorica stimata:
              <span style={{ fontSize: 15, marginLeft: 6, fontWeight: 700 }}>512 kg</span>
            </div>
            <div style={{ fontSize: 11, color: LT.n600, marginLeft: 'auto' }}>
              Residuo 800 kg − prenotato 288 kg
            </div>
          </div>

          {[
            ['Lavorazione #214 · Prosciutto crudo L.23 PT', 'Chiusa', '168 kg', 'verde'],
            ['Lavorazione #227 · Prosciutto crudo L.26 PT', 'Aperta', '120 kg', 'blu'],
          ].map(([tit, stato, qta, tono]) => (
            <div key={tit} style={{
              border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
              padding: '9px 11px', marginBottom: 7, background: LT.n0,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ flex: '1 1 240px', fontSize: 12.5, color: LT.navy, fontWeight: 600 }}>{tit}</span>
              <ChipFinto tono={tono}>{stato}</ChipFinto>
              <span style={{ fontSize: 12.5, color: LT.n700, fontWeight: 700 }}>{qta}</span>
            </div>
          ))}
        </Schermo>
      </MockFrame>

      <P>
        Le due parole che compaiono nel riquadro vanno prese alla lettera.{' '}
        <Strong>Residuo</Strong> è la quantità che avete registrato al ricevimento, mai toccata
        da nessuno. <Strong>Prenotato</Strong> è la somma di quello che le lavorazioni si sono
        prese: se avete dichiarato una quantità usata, vale quella; se non l'avete dichiarata,
        vale la stima calcolata dalla ricetta, riscalata sulla quantità prodotta.
      </P>

      <Callout kind="info" titolo="Perché “teorica”">
        Perché nessuno ha pesato niente. È un conto fatto sulle ricette. Serve a due cose: capire
        se un lotto sta finendo prima di andare in cella a guardare, e accorgersi che qualcosa
        non torna. Non serve a dire quanti chili ci sono adesso su quel bancale.
      </Callout>

      <Callout kind="warn" titolo="Se il numero è rosso">
        Vuol dire che le lavorazioni hanno prenotato <Strong>più</Strong> di quanto è entrato. Non
        è un errore del programma: quasi sempre è una quantità usata dichiarata a occhio e
        troppo alta, oppure un lotto agganciato a una lavorazione sbagliata. Si apre la
        lavorazione indicata nell'elenco e si corregge la quantità usata. Se invece a essere
        sbagliata è la quantità ricevuta, si corregge da qui — è il capitolo che segue.
      </Callout>

      <P>
        Se il lotto non è mai stato usato, il pannello dice{' '}
        <em>Nessuna lavorazione associata a questo lotto</em>. È l'informazione più utile in un
        richiamo al contrario: quel lotto non è finito in nessun prodotto.
      </P>

      {/* m5 */}
      <H id="m5" cat="temperature">5. Correggere un lotto</H>

      <P>
        Il pulsante <Tasto>Correggi</Tasto> apre una finestrella con i dati del ricevimento:{' '}
        <Strong>quantità</Strong> e unità di misura, <Strong>lotto del fornitore</Strong>,{' '}
        <Strong>scadenza</Strong> e <Strong>note</Strong>. Serve esattamente per il caso più
        comune di tutti: 120 scritto al posto di 1200 mentre il camion aspetta.
      </P>

      <Callout kind="info" titolo="Due cose non si correggono">
        L'<Strong>ingrediente</Strong> e il <Strong>lotto interno</Strong>. Il primo perché una
        riga con l'ingrediente sbagliato non è una riga da correggere, è una riga da rifare; il
        secondo perché quel codice può essere già stampato su un cartello o citato in una
        lavorazione, e cambiarlo romperebbe il collegamento. Se avete sbagliato uno dei due,
        segnate il lotto come esaurito con una nota che spiega, e registratelo di nuovo.
      </Callout>

      <Callout kind="tip" titolo="Correggere è normale, non è barare">
        La quantità in LabThree è un dato dichiarato, non un movimento contabile: si corregge
        quando ci si accorge dell'errore, senza storni né procedure. Quello che conta, davanti a
        un ispettore, è che il numero registrato sia quello vero — non che non sia mai stato
        toccato.
      </Callout>

      {/* m6 */}
      <H id="m6" cat="temperature">6. La merce di terzi</H>

      <P>
        Se lavorate <Strong>conto terzi</Strong> — ricevete merce che resta di qualcun altro, la
        lavorate e gliela restituite — i lotti di quella merce portano il contrassegno arancione{' '}
        <ChipFinto tono="arancio">Conto terzi · nome</ChipFinto> e, nella griglia dei dati, un
        riquadro <Strong>Proprietario</Strong> accanto a Fornitore.
      </P>
      <P>
        Sono due informazioni diverse e vanno lette insieme: il fornitore è chi ve l'ha portata
        (e può cambiare a ogni consegna), il proprietario è di chi è (e di solito resta lo
        stesso). Compaiono entrambi perché in un richiamo servono entrambi.
      </P>
      <P>
        Compare anche una terza tendina fra i filtri, <Tasto>Proprietà</Tasto>, con{' '}
        <Tasto>Tutta la merce</Tasto>, <Tasto>Solo merce mia</Tasto> e{' '}
        <Tasto>Solo merce di terzi</Tasto>. Serve al momento dell'inventario: separa in un
        istante quello che è vostro da quello che dovete restituire.
      </P>

      <Callout kind="info" titolo="Se non lavorate conto terzi non vedete niente di tutto questo">
        Il contrassegno e il filtro compaiono solo se in azienda esiste almeno un lotto di terzi.
        Chi compra tutto quello che lavora vede la schermata di sempre.
      </Callout>

      {/* m7 */}
      <H id="m7">Le trappole</H>

      <Callout kind="warn" titolo="La quantità non cala mai da sola">
        È la cosa che sorprende di più chi arriva da un gestionale. Un lotto usato per intero
        continua a mostrare la quantità ricevuta: quello che cambia è il consumo stimato nel
        <Tasto>Dettaglio consumi</Tasto>. Per dire "questo è finito" si usa lo stato{' '}
        <Strong>Esaurito</Strong>.
      </Callout>

      <Callout kind="warn" titolo="I lotti esauriti restano nell'elenco">
        Sbiaditi, ma ci sono, e nelle aziende avviate diventano la maggioranza. Se cercate un
        lotto attivo, filtrate <Tasto>Stato</Tasto> su <Strong>In magazzino</Strong> o{' '}
        <Strong>In uso</Strong>: la lista si accorcia moltissimo.
      </Callout>

      <Callout kind="warn" titolo="Due lotti possono avere lo stesso numero">
        Il lotto interno è unico per ingrediente, non in assoluto. Sale e pepe ricevuti lo stesso
        giorno hanno tutti e due <Code>L.180826</Code>. Non è un errore: quando cercate, cercate
        l'ingrediente più il lotto, non il lotto da solo.
      </Callout>

      <Callout kind="warn" titolo="Da qui non si aggiungono lotti">
        Un lotto nasce solo da una bolla di ingresso. Se ne manca uno, la strada è{' '}
        <Code>Bolle di ingresso</Code>: o si registra la bolla che manca, o si aggiunge la riga
        dimenticata a quella che c'è già.
      </Callout>

      {/* m8 */}
      <H id="m8">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Ho usato metà sacco. Come lo registro?">
          Non lo registrate qui, e non serve. Il consumo si dichiara nella{' '}
          <Strong>lavorazione</Strong>, dove indicate la quantità usata di ogni lotto. Il
          magazzino ne tiene conto da solo nel dettaglio consumi. Qui usate lo stato{' '}
          <Strong>In uso</Strong> per dire "questo collo è aperto".
        </FAQ>

        <FAQ q="La giacenza teorica non corrisponde a quello che vedo in cella.">
          È normale che non corrisponda al grammo: è calcolata sulle ricette, non pesata. Se la
          differenza è grossa, di solito è una quantità usata dichiarata a occhio in qualche
          lavorazione. Il <Tasto>Dettaglio consumi</Tasto> elenca quali lavorazioni hanno
          prenotato quel lotto e quanto: si parte da lì.
        </FAQ>

        <FAQ q="Posso eliminare un lotto inserito per sbaglio?">
          Non c'è un'eliminazione, ed è una scelta: un registro di rintracciabilità da cui si
          cancellano le righe non dimostra niente. Il modo corretto è correggere la quantità a
          zero con <Tasto>Correggi</Tasto>, marcarlo <Strong>esaurito</Strong> e scrivere nelle
          note perché. Resta la traccia dell'errore e della correzione, che è quello che serve.
        </FAQ>

        <FAQ q="Perché un lotto che ho appena ricevuto non compare?">
          Tre cause, in ordine di frequenza: c'è un filtro <Tasto>Stato</Tasto> attivo; state
          cercando il lotto del fornitore mentre in elenco è in evidenza quello interno (la
          ricerca li guarda entrambi, ma il filtro dev'essere vuoto); oppure la bolla è stata
          salvata senza righe articolo.
        </FAQ>

        <FAQ q="Che differenza c'è fra questa schermata e “Giacenze per ubicazione”?">
          Qui ci sono le <Strong>materie prime</Strong> ricevute, ordinate per scadenza. Là c'è
          il <Strong>prodotto in lavorazione</Strong>, ordinato per cella. Rispondono a due
          domande diverse: "cosa ho comprato" e "dove si trova adesso quello che sto
          producendo".
        </FAQ>

        <FAQ q="Un operatore base può cambiare gli stati?">
          Sì. Sono gesti di reparto e chi sta in cella deve poterli fare senza chiamare
          l'ufficio. Restano registrati come tutto il resto.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Una lavorazione dall'inizio alla fine</Strong>: dove questi lotti entrano
            davvero in produzione, e dove si dichiara quanto ne avete usato.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
